// src/utils/voiceSettingsStore.js
// Reads the user's brand settings and voices, and saves which voice is active.
// The Supabase client is injected so this can be tested in plain Node; profile.js
// wires it to the app's client.
//
// saveActiveVoice writes exactly one field, brand_settings.active_voice_id. It never
// touches public.brand_voices: an approved voice is approved for its exact stored
// text, so rewriting a voice row just to change the active selection would un-approve
// it (Handoff live voice check, 2026-10-30).

import { normalizeVoiceProfile, serializeVoiceProfile } from "./voiceProfile.js";

export function createVoiceSettingsStore({ supabase }) {
  /**
   * Fetch the user's brand profile from Supabase (auth.uid scoped).
   * Returns a shape compatible with the current UI while adding normalized voice profiles.
   */
  async function getProfile() {
    const { data: auth, error: authErr } = await supabase.auth.getUser();
    if (authErr) throw authErr;

    const userId = auth?.user?.id;
    if (!userId) return null;

    const [{ data: settings, error: sErr }, { data: voices, error: vErr }] =
      await Promise.all([
        supabase.from("brand_settings").select("*").eq("user_id", userId).maybeSingle(),
        supabase
          .from("brand_voices")
          .select("id,name,voice_text,updated_at")
          .eq("user_id", userId)
          .order("updated_at", { ascending: false }),
      ]);

    if (sErr) throw sErr;
    if (vErr) throw vErr;

    const storedSnark = settings?.snark_level ?? 0.3;

    const mappedVoices = (voices || []).map((v) => {
      const profile = normalizeVoiceProfile(v.voice_text || "", storedSnark);

      if (!profile.voice_name && v.name) {
        profile.voice_name = v.name;
      }

      return {
        id: v.id,
        name: v.name || profile.voice_name || "Voice",
        text: profile.summary || "",
        profile,
        updated_at: v.updated_at,
      };
    });

    const activeId = settings?.active_voice_id || mappedVoices[0]?.id || null;
    const active = mappedVoices.find((v) => v.id === activeId) || mappedVoices[0] || null;

    return {
      snark_level: active?.profile?.snark_level ?? storedSnark,
      active_voice_id: activeId,
      voice_name: active?.name || active?.profile?.voice_name || "",
      voices: mappedVoices,

      // Back-compat: existing callers can still send brand_voice to the worker.
      // It is now a JSON string payload the worker understands.
      brand_voice: active ? serializeVoiceProfile(active.profile) : "",

      active_voice_profile: active?.profile || null,
      active_voice_text: active?.profile?.summary || "",

      email_defaults: {
        from_name: settings?.email_from_name ?? "",
        from_address: settings?.email_from_address ?? "",
        reply_to: settings?.email_reply_to ?? "",
        provider: settings?.email_provider ?? "",
        provider_account_id: settings?.email_provider_account_id ?? "",
      },
    };
  }

  /**
   * Make one of the user's own voices the active voice (auth.uid scoped).
   * Writes only brand_settings.active_voice_id; no brand_voices row is written,
   * so every voice keeps its exact stored text and its approval.
   */
  async function saveActiveVoice(activeVoiceId) {
    if (typeof activeVoiceId !== "string" || !activeVoiceId) {
      throw new Error("Choose a voice to make active.");
    }

    const { data: auth, error: authErr } = await supabase.auth.getUser();
    if (authErr) throw authErr;

    const userId = auth?.user?.id;
    if (!userId) throw new Error("Not signed in");

    // Only a voice the caller owns can become active (RLS scopes this read too).
    const { data: voice, error: voiceErr } = await supabase
      .from("brand_voices")
      .select("id")
      .eq("user_id", userId)
      .eq("id", activeVoiceId)
      .maybeSingle();

    if (voiceErr) throw voiceErr;
    if (!voice) throw new Error("That voice wasn't found. Reload and try again.");

    const { data: saved, error: saveErr } = await supabase
      .from("brand_settings")
      .upsert({ user_id: userId, active_voice_id: activeVoiceId }, { onConflict: "user_id" })
      .select("active_voice_id")
      .single();

    if (saveErr) throw saveErr;
    if (saved?.active_voice_id !== activeVoiceId) {
      throw new Error("The active voice wasn't saved. Reload and try again.");
    }

    return activeVoiceId;
  }

  return { getProfile, saveActiveVoice };
}
